import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { SalesShell } from "@/components/crm/SalesShell";
import { EmptyState, Stat } from "@/components/crm/parts";
import { useAccounts } from "@/lib/crm/useCrm";
import {
  customerResolver,
  isMeetingLike,
  useCustomerWork,
  type AccountRef,
} from "@/lib/crm/customer-work";
import { DUE_BANDS, dueBand, formatDue, round1, taskWeeklyTotal } from "@/lib/hub";

export const Route = createFileRoute("/_authenticated/sales/customer-meetings")({
  head: () => ({
    meta: [
      { title: "Customer meetings — MyPriorities" },
      {
        name: "description",
        content:
          "Every meeting linked or tagged to a customer account, with meeting and travel time split out.",
      },
      { property: "og:title", content: "Customer meetings — MyPriorities" },
      {
        property: "og:description",
        content: "See which customers you are meeting, when, and how much time each one takes.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: CustomerMeetingsPage,
});

function CustomerMeetingsPage() {
  return (
    <SalesShell
      section="customers"
      title="Customer meetings"
      subtitle="Meetings from your Priorities work, grouped by the customer they belong to."
    >
      {({ orgId }) => <CustomerMeetings orgId={orgId} />}
    </SalesShell>
  );
}

function CustomerMeetings({ orgId }: { orgId: string }) {
  const accounts = useAccounts(orgId);
  const work = useCustomerWork(orgId);

  const refs = useMemo<AccountRef[]>(
    () => (accounts.data ?? []).map((a) => ({ id: a.id, name: a.name })),
    [accounts.data],
  );
  const resolver = useMemo(() => customerResolver(refs, work.links), [refs, work.links]);

  const grouped = useMemo(() => {
    const map = new Map<
      string,
      {
        account: AccountRef;
        open: { id: string; title: string; due: string | null; hours: number }[];
        held: { id: string; title: string; on: string; hours: number }[];
        hours: number;
      }
    >();

    const bucket = (account: AccountRef) => {
      const existing = map.get(account.id);
      if (existing) return existing;
      const created = { account, open: [], held: [], hours: 0 } as NonNullable<
        ReturnType<typeof map.get>
      >;
      map.set(account.id, created);
      return created;
    };

    for (const task of work.tasks) {
      if (task.done || !isMeetingLike(task)) continue;
      for (const account of resolver.accountsFor(task)) {
        const row = bucket(account);
        row.open.push({
          id: task.id,
          title: task.title,
          due: task.due_date,
          hours: taskWeeklyTotal(task),
        });
      }
    }

    for (const done of work.completions) {
      if (!isMeetingLike(done)) continue;
      for (const account of resolver.accountsFor(done)) {
        const row = bucket(account);
        row.hours = round1(row.hours + Number(done.hours || 0));
        if (row.held.length < 6) {
          row.held.push({
            id: done.id,
            title: done.title,
            on: done.completed_on,
            hours: Number(done.hours || 0),
          });
        }
      }
    }

    return [...map.values()].sort(
      (a, b) => b.open.length + b.held.length - (a.open.length + a.held.length),
    );
  }, [resolver, work.tasks, work.completions]);

  const upcoming = grouped.reduce((sum, row) => sum + row.open.length, 0);
  const held = work.completions.filter(isMeetingLike).length;
  const unmatched = resolver.unmatchedTags([...work.tasks, ...work.completions]);

  if (work.isLoading || accounts.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading customer meetings…</p>;
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Customers with meetings" value={String(grouped.length)} />
        <Stat label="Meetings still to hold" value={String(upcoming)} />
        <Stat label="Meetings logged" value={String(held)} hint="From completed work" />
      </div>

      {grouped.length === 0 ? (
        <EmptyState
          title="No customer meetings yet"
          body="Link a meeting to an account from the record, or tag it with a client name that matches an account."
        />
      ) : (
        <div className="space-y-3">
          {grouped.map((row) => (
            <section key={row.account.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-display text-base font-semibold">{row.account.name}</h2>
                <p className="text-xs text-muted-foreground">
                  {row.hours ? `${row.hours}h logged` : "No hours logged yet"}
                </p>
              </div>

              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <div>
                  <p className="label-caps">To hold</p>
                  {row.open.length === 0 ? (
                    <p className="mt-1 text-sm text-muted-foreground">Nothing scheduled.</p>
                  ) : (
                    <ul className="mt-1 space-y-1.5">
                      {row.open.map((task) => {
                        const band = dueBand(task.due);
                        return (
                          <li key={task.id} className="flex items-center justify-between gap-2 text-sm">
                            <span className="min-w-0 truncate">{task.title}</span>
                            <span className="flex shrink-0 items-center gap-2">
                              <span className="text-xs text-muted-foreground">{task.hours}h</span>
                              {task.due ? (
                                <Badge className={DUE_BANDS[band].className}>
                                  {formatDue(task.due)}
                                </Badge>
                              ) : (
                                <Badge variant="outline">No date</Badge>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <div>
                  <p className="label-caps">Recently held</p>
                  {row.held.length === 0 ? (
                    <p className="mt-1 text-sm text-muted-foreground">Nothing logged yet.</p>
                  ) : (
                    <ul className="mt-1 space-y-1.5">
                      {row.held.map((meeting) => (
                        <li
                          key={meeting.id}
                          className="flex items-center justify-between gap-2 text-sm"
                        >
                          <span className="min-w-0 truncate">{meeting.title}</span>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {new Date(meeting.on).toLocaleDateString("en-GB")} · {meeting.hours}h
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </section>
          ))}
        </div>
      )}

      {unmatched.length ? (
        <p className="text-xs text-muted-foreground">
          Client tags with no matching account: {unmatched.slice(0, 12).join(", ")}
          {unmatched.length > 12 ? "…" : ""}
        </p>
      ) : null}
    </div>
  );
}
